/**
 * Task board copy: the board and its type filter, and the task vocabulary
 * every card and page shares — statuses, types, sizes, priorities and the
 * execution (dependency) states.
 */

export interface TaskBoardCopy {
  readonly board: {
    readonly title: string;
    readonly propose: string;
    readonly columnCount: (count: number) => string;
    readonly emptyColumn: string;
    readonly statusTabsLabel: string;
    readonly boardLabel: string;
  };

  readonly typeFilter: {
    /** The accessible name of the task-type filter group. */
    readonly label: string;
    readonly all: string;
  };

  readonly statuses: Readonly<Record<string, string>>;

  readonly types: Readonly<Record<string, string>>;

  readonly difficulties: Readonly<Record<string, string>>;

  readonly priorities: Readonly<Record<string, string>>;

  readonly execution: {
    readonly independent: string;
    readonly cleared: string;
    readonly blockedBy: string;
    readonly more: (count: number) => string;
    readonly dependencies: string;
    readonly blocks: string;
    readonly noDependencies: string;
    readonly blocksNothing: string;
    readonly editDependencies: string;
    readonly editDependenciesBody: string;
    readonly editDependenciesEffects: readonly string[];
    readonly noCandidates: string;
    readonly approvalBlocked: string;
    readonly blockedBadge: string;
  };
}

export const TASK_BOARD_COPY_EN: TaskBoardCopy = {
  board: {
    title: 'Task board',
    propose: 'Propose a task',
    columnCount: (count) => `${count} ${count === 1 ? 'task' : 'tasks'}`,
    emptyColumn: 'Nothing here.',
    statusTabsLabel: 'Task status',
    boardLabel: 'Task board by status',
  },

  typeFilter: {
    label: 'Task type',
    all: 'All',
  },

  statuses: {
    BACKLOG: 'Backlog',
    READY: 'Ready',
    IN_PROGRESS: 'In progress',
    IN_REVIEW: 'In review',
    DONE: 'Done',
    REJECTED: 'Rejected',
  },

  types: {
    DEVELOPMENT: 'Development',
    // "R&D" is what the team calls it; the stored value stays explicit.
    RESEARCH_AND_DEVELOPMENT: 'R&D',
    ANNOTATION: 'Annotation',
  },
  // Sizes. The stored vocabulary keeps its 0009 names; these are labels.

  difficulties: {
    EXTRA_SMALL: 'XS',
    SMALL: 'S',
    MEDIUM: 'M',
    LARGE: 'L',
    EXTRA_LARGE: 'XL',
  },

  priorities: {
    P1: 'P1 · Urgent',
    P2: 'P2 · High',
    P3: 'P3 · Medium',
    P4: 'P4 · Low',
  },

  execution: {
    independent: 'Parallel',
    cleared: 'Dependencies cleared',
    blockedBy: 'Blocked by',
    more: (count) => `+${String(count)} more`,
    dependencies: 'Dependencies',
    blocks: 'Blocks',
    noDependencies: 'No prerequisites — this task can run in parallel.',
    blocksNothing: 'Nothing waits on this task.',
    editDependencies: 'Edit dependencies',
    editDependenciesBody:
      'Choose the tasks this one must wait for. Editable only while it is in the Backlog.',
    editDependenciesEffects: [
      'The task cannot be approved into Ready until every prerequisite is Done.',
      'The change is recorded in the task history.',
    ],
    noCandidates: 'No other tasks are available to depend on.',
    approvalBlocked:
      'This task has unresolved prerequisites, so it cannot be approved yet. Ready means startable.',
    blockedBadge: 'Blocked',
  },
};

export const TASK_BOARD_COPY_AR: TaskBoardCopy = {
  board: {
    title: 'لوحة المهام',
    propose: 'اقتراح مهمة',
    columnCount: (count) => (count === 1 ? 'مهمة واحدة' : `${count} مهام`),
    emptyColumn: 'لا شيء هنا.',
    statusTabsLabel: 'حالة المهمة',
    boardLabel: 'لوحة المهام حسب الحالة',
  },

  typeFilter: {
    label: 'نوع المهمة',
    all: 'الكل',
  },

  statuses: {
    BACKLOG: 'المقترحات',
    READY: 'جاهزة',
    IN_PROGRESS: 'قيد التنفيذ',
    IN_REVIEW: 'قيد المراجعة',
    DONE: 'منجزة',
    REJECTED: 'مرفوضة',
  },

  types: {
    DEVELOPMENT: 'تطوير',
    // يبقى الاختصار اللاتيني كما ينطقه الفريق، معزولًا LTR في الواجهة.
    RESEARCH_AND_DEVELOPMENT: 'بحث وتطوير',
    ANNOTATION: 'تصنيف',
  },
  // الأحجام تُعرض بالاختصارات اللاتينية نفسها، معزولة LTR في الواجهة.

  difficulties: {
    EXTRA_SMALL: 'XS',
    SMALL: 'S',
    MEDIUM: 'M',
    LARGE: 'L',
    EXTRA_LARGE: 'XL',
  },

  priorities: {
    P1: 'P1 · عاجلة',
    P2: 'P2 · عالية',
    P3: 'P3 · متوسطة',
    P4: 'P4 · منخفضة',
  },

  execution: {
    independent: 'قابلة للتوازي',
    cleared: 'اكتملت المتطلبات',
    blockedBy: 'بانتظار',
    more: (count) => `+${String(count)} أخرى`,
    dependencies: 'المتطلبات السابقة',
    blocks: 'تعيق',
    noDependencies: 'لا متطلبات سابقة — يمكن تنفيذ هذه المهمة بالتوازي.',
    blocksNothing: 'لا شيء ينتظر هذه المهمة.',
    editDependencies: 'تعديل المتطلبات',
    editDependenciesBody:
      'اختر المهام التي يجب أن تنتظرها هذه المهمة. التعديل ممكن فقط وهي في المقترحات.',
    editDependenciesEffects: [
      'لا يمكن اعتماد المهمة إلى «جاهزة» حتى تُنجز كل متطلباتها.',
      'يُسجَّل التغيير في سجل المهمة.',
    ],
    noCandidates: 'لا توجد مهام أخرى يمكن الاعتماد عليها.',
    approvalBlocked:
      'لهذه المهمة متطلبات غير منجزة، فلا يمكن اعتمادها بعد. «جاهزة» تعني قابلة للبدء.',
    blockedBadge: 'محجوبة',
  },
};
